import { fetchLogIn, fetchPreregister, selectRecovery } from './loginSlice'
import { getPhoneParts, parseInternational } from 'utils/phone'
import { ReactComponent as Account } from 'assets/account.svg'
import { ReactComponent as Padlock } from 'assets/padlock.svg'
import { updateValues } from 'containers/forms/formSlice'
import { useDispatch, useSelector } from 'react-redux'
import PhoneInput from 'components/inputs/PhoneInput'
import { useEffect, useRef, useState } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import { useHistory } from 'react-router-dom'
import { finalExp } from 'utils/validation'
import Input from 'components/inputs/Input'

/** Wartość pola e-mail, która wygląda na numer telefonu (tylko cyfry, opcjonalnie "+" na początku) */
const PHONE_LIKE = /^\+?[\d\s()-]*$/
/**
 * Ile ms bez zmian loginu, zanim wyślemy formularz. Safari wpisuje login z podpowiedzi
 * znak po znaku i potrafi „wcisnąć” Enter, zanim skończy — bez tego poszedłby niepełny e-mail.
 */
const SETTLE_MS = 400

const Form = ({ callback, isPassword = false, showMailForm = false, setMailForm, showPassword, showRegistrationForm }) => {
  const [isCrmUser, setCrmUser] = useState(false)
  const [isSending, setSending] = useState(false)
  const [form, setForm] = useState(() => {
    const query = new URLSearchParams(window.location.search)
    const login = query.get('login')
    if (!login) return { username: '', password: '' }
    if (showMailForm) return { username: login, password: '' }
    const { prefix, phone } = getPhoneParts(login)
    return { username: phone, prefix, password: '' }
  })
  const [warning, setWarning] = useState({
    username: false,
    password: false,
  })

  const refs = []
  const usernameRef = useRef(null)
  const autoSwitchRef = useRef(false)
  const autoMailRef = useRef(false)
  const prevMailFormRef = useRef(showMailForm)
  /** Aktualne wartości dla akcji odłożonych w czasie (setTimeout widzi stare domknięcie) */
  const formRef = useRef(form)
  formRef.current = form
  const showMailFormRef = useRef(showMailForm)
  showMailFormRef.current = showMailForm
  const sendingRef = useRef(isSending)
  sendingRef.current = isSending
  const lastUsernameChangeRef = useRef(0)
  const settleTimerRef = useRef()

  useEffect(() => () => clearTimeout(settleTimerRef.current), [])

  const markUsernameChange = () => {
    lastUsernameChangeRef.current = Date.now()
  }

  /** Uruchamia akcję dopiero, gdy login przestanie się zmieniać (np. skończy się autouzupełnianie) */
  const whenSettled = action => {
    clearTimeout(settleTimerRef.current)
    const wait = SETTLE_MS - (Date.now() - lastUsernameChangeRef.current)
    if (wait > 0) settleTimerRef.current = setTimeout(() => whenSettled(action), wait)
    else action()
  }

  useEffect(() => {
    // porównanie z poprzednią wartością zamiast flagi „pierwszy render” — odporne na podwójne efekty w StrictMode
    if (prevMailFormRef.current === showMailForm) return
    prevMailFormRef.current = showMailForm
    if (autoSwitchRef.current) {
      autoSwitchRef.current = false
      const input = usernameRef.current
      if (!input) return
      input.focus()
      try {
        const end = input.value.length
        input.setSelectionRange(end, end)
      } catch { /* type bez obsługi zaznaczenia */ }
    } else {
      // przełączenie przyciskiem
      autoMailRef.current = false
      setForm({ username: '', password: '' })
    }
  }, [showMailForm])

  /** Prefiks z pola telefonu — pamiętany, żeby po powrocie z e-maila nie wracać do domyślnego kraju */
  const lastPrefixRef = useRef(form.prefix)

  const switchLoginType = (toMail, username) => {
    markUsernameChange()
    autoSwitchRef.current = true
    autoMailRef.current = toMail
    // hasło wpisane przez autouzupełnianie (Safari wypełnia login i hasło naraz) — zostawiamy je i pole hasła
    const keepPassword = isPassword && !!form.password
    if (isPassword && !keepPassword) showPassword(false)
    if (toMail) {
      lastPrefixRef.current = form.prefix
      setForm({ username, password: keepPassword ? form.password : '' })
    } else {
      const { prefix, phone } = parseInternational(username) || { prefix: lastPrefixRef.current, phone: username.replace(/\D/g, '') }
      setForm({ username: phone, prefix, password: keepPassword ? form.password : '' })
    }
    setMailForm(toMail)
  }

  const recovery = useSelector(selectRecovery)
  const { push } = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation('form')

  const checkFormData = () => whenSettled(preregister)

  const preregister = async () => {
    if (sendingRef.current) return
    const form = formRef.current
    const showMailForm = showMailFormRef.current
    if (showMailForm && form.username && !finalExp.mail.test(form.username)) {
      setWarning({ username: 'wrongEmail' })
      return
    }
    if (form.username) {
      setWarning({})
      setSending(true)
      try {
        const body = showMailForm
          ? { email: form.username }
          : { prefix: form.prefix, phone: form.username }
        const response = await dispatch(fetchPreregister(body)).unwrap()
        setSending(false)
        if (response.is_crm_user && window.location.host === 'app-demo.boxomaty.pl') {
          setCrmUser(true)
        }
        if (showMailForm
          ? response.email_exists
          : response.phone_exists
        ) {
          showPassword(true)
        } else {
          dispatch(updateValues(showMailForm
            ? { mail: form.username }
            : { prefix: form.prefix, phone: form.username }))
          showRegistrationForm()
        }

      } catch (err) {
        setSending(false)
        console.warn('Fetch preregister failed')
      }
    } else {
      setWarning({
        username: true
      })
    }
  }

  const changeHandler = e => {
    if (!e) return
    const { name, value } = e.target

    if (name === 'username' && showMailForm && setMailForm && autoMailRef.current && PHONE_LIKE.test(value)) {
      switchLoginType(false, value)
      return
    }

    if (name === 'username') markUsernameChange()
    setForm({
      ...form,
      [name]: value.replace(/\s/g, '')
    })
    if (isPassword && name === 'username') showPassword(false)
    if (value && warning[name]) setWarning({
      ...warning,
      [name]: false
    })
  }

  const enterClicked = ref => {
    if (!isPassword) {
      checkFormData()
    } else {
      const index = refs.findIndex(r => Object.is(r, ref))
      if (index === -1) return
      if (refs[index + 1]) refs[index + 1].focus()
      else if (index + 1 === refs.length) submit()
    }
  }

  const submit = () => whenSettled(logIn)

  const logIn = () => {
    if (sendingRef.current) return
    const form = formRef.current
    const { username, password } = form
    if (username && password) {
      setSending(true)
      dispatch(fetchLogIn(form)).then(({ payload }) => {
        callback?.(payload)
        setSending(false)
      })

    } else setWarning({
      username: !username,
      password: !password
    })
  }

  return (
    <form
      action='#'
      method='post'
      className='login__form'
      onSubmit={e => {
        e.preventDefault()
      }}>
      {isCrmUser && <div className='login__form__info'>
        {t('yourNumberIsLinkedToACrmOperatorAccount-warning')}
      </div>}

      {showMailForm
        ? <Input
          disabled={isSending}
          ref={r => {
            refs.push(r)
            usernameRef.current = r
          }}
          value={form.username}
          name='username'
          autoComplete='username'
          label={t('email')}
          inputMode='email'
          minLength='1'
          maxLength='200'
          icon={<Account />}
          warning={warning.username ? t(typeof warning.username === 'string' ? warning.username : 'enterEmail') : undefined}
          placeholder={t('email')}
          enterClicked={enterClicked}
          changeHandler={changeHandler} />
        : <PhoneInput
          disabled={isSending}
          ref={r => {
            refs.push(r)
            usernameRef.current = r
          }}
          value={form.username}
          prefix={form.prefix}
          name='username'
          autoComplete='username'
          label={t('phoneNumber')}
          minLength='1'
          maxLength='200'
          warning={warning.username ? t('enterPhoneNumber') : undefined}
          placeholder={t('phoneNumber')}
          enterClicked={enterClicked}
          onNonPhoneInput={setMailForm && (raw => {
            switchLoginType(true, raw)
          })}
          setValue={value => {
            markUsernameChange()
            if (isPassword) showPassword(false)
            setForm({
              password: '',
              prefix: value.prefix,
              username: value.phone
            })
          }} />}

      {isPassword && <>
        <Input
          disabled={isSending}
          ref={r => refs.push(r)}
          value={form.password}
          name='password'
          type='password'
          autoComplete='current-password'
          label={t('password')}
          minLength='6'
          maxLength='90'
          placeholder={t('password')}
          icon={<Padlock />}
          warning={warning.password ? t('enterPassword') : undefined}
          enterClicked={enterClicked}
          changeHandler={changeHandler} />
        <div
          className='login__form__reminder'
          onClick={() => push(recovery ? '/login/recovery' : '/login/reset')}>
          {t('forgotYourPassword')}
        </div>
      </>}

      {isPassword
        ? <ArrowBtn
          enlarged
          text={t('logIn')}
          isLoading={isSending}
          action={submit}
        /> : <ArrowBtn
          enlarged
          text={t('next')}
          isLoading={isSending}
          action={checkFormData}
        />}
    </form>
  )
}

export default Form